import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Target, Plus, Search, ChevronRight, Trophy,
  TrendingUp, Clock, XCircle, DollarSign, X, User, Trash2, AlertCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import ClientTagPicker from '../components/ClientTagPicker';
import SmartRouterBanner from '../components/SmartRouter/SmartRouterBanner';
import { useOrchestratorContext } from '../hooks/useOrchestratorContext';
import { completeOrchestratorStep } from '../services/smartStartApi';

interface Engagement {
  id: number;
  name: string;
  client_name: string | null;
  stage: string;
  budget_cap: number | null;
  notes: string | null;
  smart_start_session_id?: number | null;
  created_at: string;
  updated_at: string;
  owner?: { id: number; name: string; email: string };
}

const FORWARDED_ORCHESTRATOR_KEYS = [
  'smartStartSessionId', 'stepIndex', 'stepKey', 'subStep', 'orchestratorToken', 'returnTo',
];

function buildOrchestratorForwardSearch(search: string): string {
  const incoming = new URLSearchParams(search);
  const out = new URLSearchParams();
  for (const k of FORWARDED_ORCHESTRATOR_KEYS) {
    const v = incoming.get(k);
    if (v != null && v !== '') out.set(k, v);
  }
  const qs = out.toString();
  return qs ? `?${qs}` : '';
}

interface Counts {
  active: number;
  won: number;
  lost: number;
  total: number;
}

const STAGE_LABELS: Record<string, string> = {
  intake: 'Intake',
  synthesis: 'Synthesis',
  ou_oa: 'OU/OA Deck',
  plan: 'Project Plan',
  datasim: 'DataSim',
  sow: 'SOW',
  won: 'Won',
  lost: 'Lost',
  paused: 'Paused',
};

const STAGE_COLORS: Record<string, string> = {
  intake: 'bg-gray-100 text-gray-700',
  synthesis: 'bg-blue-100 text-blue-700',
  ou_oa: 'bg-purple-100 text-purple-700',
  plan: 'bg-yellow-100 text-yellow-800',
  datasim: 'bg-orange-100 text-orange-700',
  sow: 'bg-green-100 text-green-700',
  won: 'bg-emerald-100 text-emerald-700',
  lost: 'bg-red-100 text-red-700',
  paused: 'bg-gray-100 text-gray-500',
};

const STAGE_ORDER = ['intake', 'synthesis', 'ou_oa', 'plan', 'datasim', 'sow', 'won'];

const getStageProgress = (stage: string) => {
  const idx = STAGE_ORDER.indexOf(stage);
  if (idx <= 0) return 0;
  return Math.round((idx / (STAGE_ORDER.length - 1)) * 100);
};

// ─── New Engagement Modal ───────────────────────────────────────────────────────
// Branding status for the selected client tag — drives the banner that
// tells the PM whether the tag has its own logo/palette configured or
// will fall back to Stemmler defaults.
type BrandingStatus =
  | { state: 'idle' }
  | { state: 'loading' }
  | { state: 'configured'; companyName: string; primary: string; hasLogo: boolean }
  | { state: 'defaults' }
  | { state: 'error'; message: string };

const NewEngagementModal: React.FC<{
  onClose: () => void;
  onCreate: (e: Engagement) => void;
  initialName?: string;
  smartStartSessionId?: number | null;
}> = ({ onClose, onCreate, initialName = '', smartStartSessionId = null }) => {
  const [form, setForm] = useState({
    name: initialName,
    budget_cap: '',
    notes: '',
  });
  const [clientTagId, setClientTagId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [brandingStatus, setBrandingStatus] = useState<BrandingStatus>({ state: 'idle' });

  // Fetch branding for the selected tag so the modal can show whether the
  // client has its own branding (✓ configured) or will inherit Stemmler
  // defaults (⚠ no branding). Re-runs every time the tag changes.
  useEffect(() => {
    if (!clientTagId) {
      setBrandingStatus({ state: 'idle' });
      return;
    }
    let cancelled = false;
    setBrandingStatus({ state: 'loading' });
    api
      .get(`/api/client-tags/${clientTagId}/branding`)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data;
        if (data?.success && data?.data) {
          // A row exists. Distinguish "real configuration" (user-set primary
          // colour != the Stemmler default) from "auto-created with Stemmler
          // defaults" — both have data, but only the first deserves the green
          // banner. The default palette uses #3B82F6 from getDefaultBranding().
          const isDefault =
            (data.data.primary_color || '').toLowerCase() === '#3b82f6' &&
            !data.data.logo_s3_key;
          if (isDefault) {
            setBrandingStatus({ state: 'defaults' });
          } else {
            setBrandingStatus({
              state: 'configured',
              companyName: data.data.company_name || 'Client',
              primary: data.data.primary_color || '#3B82F6',
              hasLogo: Boolean(data.data.logo_s3_key),
            });
          }
        } else {
          setBrandingStatus({ state: 'defaults' });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setBrandingStatus({
          state: 'error',
          message: err?.response?.data?.error || err?.message || 'Could not check branding',
        });
      });
    return () => { cancelled = true; };
  }, [clientTagId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      setLoading(true);
      const res = await api.post('/api/engagements', {
        name: form.name.trim(),
        budget_cap: form.budget_cap ? parseFloat(form.budget_cap) : null,
        notes: form.notes.trim() || null,
        client_tag_id: clientTagId ?? undefined,
        smart_start_session_id: smartStartSessionId ?? undefined,
      });
      onCreate(res.data);
      toast.success('Engagement created!');
      onClose();
    } catch (err: any) {
      const apiErr = err?.response?.data?.error || err?.response?.data?.message;
      toast.error(apiErr || 'Failed to create engagement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
              <Target className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">New Engagement</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Engagement Name <span className="text-red-500">*</span>
            </label>
            <input
              autoFocus
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Pacific Life — Demand Forecasting"
              required
            />
          </div>
          <ClientTagPicker
            label="Client"
            value={clientTagId}
            onChange={setClientTagId}
            allowCreate
            helpText="Picks the client for cost attribution and branding. Use ‘+ Add new client’ to create one inline."
          />

          {/* Branding-status banner. Three states:
               · configured  → green, name + color swatch + logo indicator
               · defaults    → amber, "no branding set, will use Stemmler defaults" + Add link
               · loading/idle/error → silent or quiet helper text                     */}
          {clientTagId && brandingStatus.state === 'loading' && (
            <div className="text-xs text-gray-500 flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-gray-300 animate-pulse" />
              Checking branding…
            </div>
          )}

          {clientTagId && brandingStatus.state === 'configured' && (
            <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800 flex items-center gap-2">
              <span
                className="inline-block w-3 h-3 rounded-sm border border-green-300"
                style={{ backgroundColor: brandingStatus.primary }}
                title={brandingStatus.primary}
              />
              <span className="font-medium">Branding ready</span>
              <span className="text-green-700">— {brandingStatus.companyName} {brandingStatus.hasLogo ? '(logo + palette)' : '(palette only)'}</span>
            </div>
          )}

          {clientTagId && brandingStatus.state === 'defaults' && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-start justify-between gap-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-medium">No branding configured</div>
                  <div className="text-amber-700">Exports will use Stemmler defaults until a logo and colors are added.</div>
                </div>
              </div>
              <a
                href={`/app/admin?tab=branding&tag=${clientTagId}`}
                target="_blank"
                rel="noreferrer"
                className="flex-shrink-0 text-amber-900 underline hover:text-amber-950 font-medium whitespace-nowrap"
              >
                Add now →
              </a>
            </div>
          )}

          {clientTagId && brandingStatus.state === 'error' && (
            <div className="text-xs text-gray-500">
              Could not check branding: {brandingStatus.message}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Budget Cap ($)</label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input
                type="number"
                className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={form.budget_cap}
                onChange={e => setForm(f => ({ ...f, budget_cap: e.target.value }))}
                placeholder="500000"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none overflow-hidden"
              value={form.notes}
              onChange={e => {
                setForm(f => ({ ...f, notes: e.target.value }));
                e.target.style.height = 'auto';
                e.target.style.height = e.target.scrollHeight + 'px';
              }}
              placeholder="Initial context or background..."
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !form.name.trim()}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Engagement'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Main Page ──────────────────────────────────────────────────────────────────
const BDEngagements: React.FC = () => {
  const navigate = useNavigate();
  const orchestratorCtx = useOrchestratorContext();
  const [engagements, setEngagements] = useState<Engagement[]>([]);
  const [counts, setCounts] = useState<Counts>({ active: 0, won: 0, lost: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const autoOpenedRef = useRef(false);

  const orchestratorPrefillName = orchestratorCtx.extraParams.engagement_name || '';
  const orchestratorPrefillClient = orchestratorCtx.extraParams.client_name || '';

  const fetchEngagements = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (search) params.search = search;
      if (stageFilter) params.stage = stageFilter;
      const res = await api.get('/api/engagements', { params });
      setEngagements(res.data.engagements);
      setCounts(res.data.counts);
    } catch {
      toast.error('Failed to load engagements');
    } finally {
      setLoading(false);
    }
  }, [search, stageFilter]);

  useEffect(() => { fetchEngagements(); }, [fetchEngagements]);

  useEffect(() => {
    if (autoOpenedRef.current) return;
    if (
      orchestratorCtx.inRouter
      && orchestratorCtx.stepKey === 'create_or_pick_engagement'
      && !loading
    ) {
      autoOpenedRef.current = true;
      setShowModal(true);
    }
  }, [orchestratorCtx.inRouter, orchestratorCtx.stepKey, loading]);

  const completeStep1IfRouted = useCallback(async (engagementId: number) => {
    if (
      !orchestratorCtx.inRouter
      || orchestratorCtx.sessionId == null
      || orchestratorCtx.stepIndex == null
      || !orchestratorCtx.token
      || orchestratorCtx.stepKey !== 'create_or_pick_engagement'
    ) return;
    try {
      await completeOrchestratorStep(orchestratorCtx.sessionId, orchestratorCtx.stepIndex, {
        token: orchestratorCtx.token,
        artifact: { engagement_id: engagementId, stage: 'intake' },
        summary: `Engagement #${engagementId} linked to Smart Router session.`,
      });
    } catch (_e) { /* non-fatal — banner finish still works */ }
  }, [orchestratorCtx.inRouter, orchestratorCtx.sessionId, orchestratorCtx.stepIndex, orchestratorCtx.token, orchestratorCtx.stepKey]);

  const handleCreate = async (e: Engagement) => {
    setEngagements(prev => [e, ...prev]);
    setCounts(prev => ({ ...prev, active: prev.active + 1, total: prev.total + 1 }));
    await completeStep1IfRouted(e.id);
    const forward = buildOrchestratorForwardSearch(window.location.search);
    navigate(`/app/bd-engagements/${e.id}${forward}`);
  };

  const handlePickExisting = async (id: number) => {
    await completeStep1IfRouted(id);
    const forward = buildOrchestratorForwardSearch(window.location.search);
    navigate(`/app/bd-engagements/${id}${forward}`);
  };

  const handleDelete = async (id: number) => {
    try {
      setDeletingId(id);
      await api.delete(`/api/engagements/${id}`);
      setEngagements(prev => prev.filter(e => e.id !== id));
      setCounts(prev => ({ ...prev, total: prev.total - 1, active: Math.max(0, prev.active - 1) }));
      toast.success('Engagement deleted');
    } catch {
      toast.error('Failed to delete engagement');
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  const stats = [
    { label: 'Active', value: counts.active, icon: TrendingUp, color: 'text-blue-600 bg-blue-50' },
    { label: 'Won', value: counts.won, icon: Trophy, color: 'text-emerald-600 bg-emerald-50' },
    { label: 'Lost', value: counts.lost, icon: XCircle, color: 'text-red-500 bg-red-50' },
    { label: 'Total', value: counts.total, icon: Target, color: 'text-purple-600 bg-purple-50' },
  ];

  return (
    <>
    {orchestratorCtx.inRouter && (
      <SmartRouterBanner
        stepLabel="Create or pick a BD engagement"
        canFinish={false}
        disabledReason="Open an engagement first — the “Finish” button lives on the engagement detail screen."
        buildCompletionPayload={() => null}
        buildResumeExtra={() => ({ subStep: 'list' })}
      />
    )}
    <div className="max-w-6xl mx-auto px-6 py-8">
      {orchestratorCtx.inRouter && orchestratorCtx.stepKey === 'create_or_pick_engagement' && (
        <div className="mb-6 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-5">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-white rounded-lg flex-shrink-0">
              <Target className="w-5 h-5 text-indigo-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-indigo-900 mb-1">
                Smart Router — Step 1 of 7: Create or pick the engagement
              </h3>
              <p className="text-sm text-indigo-800 mb-3">
                {orchestratorPrefillName ? (
                  <>
                    Start a new BD engagement for <span className="font-semibold">"{orchestratorPrefillName}"</span>
                    {orchestratorPrefillClient && <> — client <span className="font-semibold">{orchestratorPrefillClient}</span></>}
                    , or open one of the existing engagements below to continue.
                  </>
                ) : (
                  <>Create a new BD engagement, or open an existing one below.</>
                )}
              </p>
              <button
                onClick={() => setShowModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 shadow-sm"
              >
                <Plus className="w-4 h-4" />
                Create new engagement
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl">
            <Target className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Business Development Pipeline</h1>
            <p className="text-sm text-gray-500">
              Manage business development engagements from discovery to signed SOW
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 shadow-sm"
        >
          <Plus className="w-4 h-4" /> New Engagement
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        {stats.map(s => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-white border border-gray-200 rounded-xl p-5 flex items-center gap-4">
              <div className={`p-3 rounded-xl ${s.color}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                <p className="text-sm text-gray-500">{s.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Search engagements..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
          value={stageFilter}
          onChange={e => setStageFilter(e.target.value)}
        >
          <option value="">All Stages</option>
          {Object.entries(STAGE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {/* Engagements List */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : engagements.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-xl p-16 text-center">
          <Target className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium text-gray-500">No engagements yet</p>
          <p className="text-sm text-gray-400 mt-1 mb-6">
            Create your first BD engagement to get started
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" /> New Engagement
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {engagements.map(eng => {
            const progress = getStageProgress(eng.stage);
            const isTerminal = ['won', 'lost', 'paused'].includes(eng.stage);
            const initials = (eng.client_name || eng.name).slice(0, 2).toUpperCase();
            return (
              <div
                key={eng.id}
                onClick={() => handlePickExisting(eng.id)}
                className="bg-white border border-gray-200 rounded-xl hover:shadow-md hover:border-blue-200 transition-all cursor-pointer group"
              >
                <div className="flex items-center gap-4 px-5 py-4">

                  {/* Client avatar */}
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                    {initials}
                  </div>

                  {/* Main content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-gray-900 text-sm truncate">{eng.name}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-md font-medium flex-shrink-0 ${STAGE_COLORS[eng.stage] || 'bg-gray-100 text-gray-600'}`}>
                        {STAGE_LABELS[eng.stage] || eng.stage}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                      {eng.client_name && <span className="text-gray-500 font-medium">{eng.client_name}</span>}
                      {eng.owner && <span className="flex items-center gap-1"><User className="w-3 h-3" />{eng.owner.name}</span>}
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(eng.updated_at).toLocaleDateString('en-GB')}</span>
                    </div>
                  </div>

                  {/* Progress */}
                  {!isTerminal && (
                    <div className="w-24 flex-shrink-0 hidden md:block">
                      <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                        <span>Progress</span>
                        <span className="font-medium text-gray-600">{progress}%</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-blue-400 to-purple-500 rounded-full" style={{ width: `${progress}%` }} />
                      </div>
                    </div>
                  )}

                  {/* Budget */}
                  {eng.budget_cap && (
                    <div className="text-right flex-shrink-0 hidden sm:block">
                      <p className="text-sm font-bold text-gray-800">${Number(eng.budget_cap).toLocaleString()}</p>
                      <p className="text-xs text-gray-400">Budget cap</p>
                    </div>
                  )}

                  {/* Delete + chevron */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {confirmDeleteId === eng.id ? (
                      <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                        <span className="text-xs text-red-600 font-medium">Delete?</span>
                        <button onClick={() => handleDelete(eng.id)} disabled={deletingId === eng.id} className="px-2 py-1 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-1">
                          {deletingId === eng.id ? <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Deleting...</> : 'Yes'}
                        </button>
                        <button onClick={() => setConfirmDeleteId(null)} className="px-2 py-1 border border-gray-300 text-gray-600 rounded text-xs font-medium hover:bg-gray-50">No</button>
                      </div>
                    ) : (
                      <button
                        onClick={e => { e.stopPropagation(); setConfirmDeleteId(eng.id); }}
                        className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-500 transition-colors" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <NewEngagementModal
          onClose={() => setShowModal(false)}
          onCreate={handleCreate}
          initialName={orchestratorPrefillName}
          smartStartSessionId={orchestratorCtx.sessionId}
        />
      )}
    </div>
    </>
  );
};

export default BDEngagements;
